/**
 * Escaping helpers for serialized template output.
 *
 * Components use these when building initial markup; mounted updates should
 * still prefer DOM properties such as textContent, value, and className.
 */
const HTML_ESCAPE_MAP = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
};
const HTML_ESCAPE_PATTERN = /[&<>"']/g;
/**
 * Escapes a value for text nodes.
 */
export const escapeText = (value) => {
    if (value === null || value === undefined)
        return '';
    return String(value).replace(HTML_ESCAPE_PATTERN, (char) => HTML_ESCAPE_MAP[char] ?? char);
};
/**
 * Escapes a value for quoted attribute values.
 */
export const escapeAttribute = escapeText;
// URL-bearing attributes whose javascript:/vbscript:/data: values execute or
// navigate (mirrors utils/sanitizer.ts URL_ATTRIBUTES — the sanitizer tree
// walk applies the same neutralization to full documents).
const URL_ATTRIBUTES = new Set([
    'action',
    'background',
    'data',
    'formaction',
    'href',
    'ping',
    'poster',
    'src',
    'srcset',
    'xlink:href',
]);
const UNSAFE_URL_SCHEME = /^(?:javascript|vbscript):/;
const DATA_URL = /^data:/;
const SAFE_DATA_URL = /^data:image\//;
/**
 * Neutralizes URL-bearing attribute values whose scheme can execute script or
 * navigate: `href=${userHref}` with a `javascript:` payload must not serialize
 * as a live link on the string (SSR) path or commit via setAttribute on the
 * DOM path. An unsafe value collapses to the empty string — the same visible
 * `href=""` contract the sanitizer's tree walk uses. Non-URL attributes pass
 * through untouched (#1524).
 */
export function safeUrlAttribute(attrName, value) {
    if (!URL_ATTRIBUTES.has(attrName.toLowerCase()))
        return value;
    const normalized = value.replace(/[\u0000-\u001F\u007F\s]+/g, '').toLowerCase();
    if (UNSAFE_URL_SCHEME.test(normalized))
        return '';
    if (DATA_URL.test(normalized) && !SAFE_DATA_URL.test(normalized))
        return '';
    return value;
}
/**
 * Runtime brand for trusted markup. Type-level brand is separate (unique symbol)
 * so only values produced by {@link rawHtml} satisfy `RawHtml`.
 */
const RAW_HTML_SYMBOL = Symbol.for('hydrogen.rawHtml');
export const rawHtml = (value) => {
    const html = value === null || value === undefined ? '' : String(value);
    return {
        [RAW_HTML_SYMBOL]: true,
        html,
        toString() {
            return html;
        },
    };
};
/**
 * Detects values produced by rawHtml().
 */
export const isRawHtml = (value) => {
    return !!(value &&
        typeof value === 'object' &&
        value[RAW_HTML_SYMBOL]);
};
//# sourceMappingURL=html-escape.js.map